import { useEffect, useMemo, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import type {
  CompanyRepSummary,
  CompanyRepSummaryRow,
} from "@shared-core/data/api-types";
import { Card } from "../app/components/primitives/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { currentTime } from "@shared-core/domain/current-time";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function pctAchieved(row: CompanyRepSummaryRow): number {
  return row.pct_achieved != null ? Math.round(row.pct_achieved) : 0;
}

function pctColorClass(pct: number): string {
  if (pct >= 100) return "text-green-600";
  if (pct >= 85) return "text-foreground";
  return "text-red-600";
}

/**
 * Company + per-rep contribution/revenue/target figures, one table
 * (sales-dashboard-hub, product brief §5 point 4). Backed by
 * GET /targets/company-rep-summary — always the latest published plan.
 */
export function RepCompanyFiguresPage() {
  const dataAdapter = useDataAdapter();
  const now = currentTime();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedRepId, setSelectedRepId] = useState<string>("all");
  const [figures, setFigures] = useState<CompanyRepSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    dataAdapter
      .getCompanyRepSummary(year, month)
      .then((result) => {
        if (!cancelled) setFigures(result);
      })
      .catch(() => {
        if (!cancelled) {
          setFigures(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, year, month]);

  const monthOptions = useMemo(() => {
    const options: { year: number; month: number; label: string }[] = [];
    for (let i = 0; i < 12; i++) {
      const idx = year * 12 + (month - 1) - i;
      const y = Math.floor(idx / 12);
      const m = (idx % 12) + 1;
      options.push({ year: y, month: m, label: `${MONTH_NAMES[m - 1]} ${y}` });
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computed once from mount-time now; year/month deps would regenerate the same window
  }, []);

  const visibleReps =
    !figures || selectedRepId === "all"
      ? (figures?.reps ?? [])
      : figures.reps.filter((r) => r.user_id === selectedRepId);

  const rows: CompanyRepSummaryRow[] = figures
    ? [figures.company, ...visibleReps]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Rep vs company figures</h1>
        <p className="text-sm text-muted-foreground">
          Contribution, revenue and progress against target — company-wide and
          by rep
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={`${year}-${month}`}
          onValueChange={(v) => {
            const [y, m] = v.split("-").map(Number);
            setYear(y);
            setMonth(m);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((opt) => (
              <SelectItem
                key={`${opt.year}-${opt.month}`}
                value={`${opt.year}-${opt.month}`}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedRepId} onValueChange={setSelectedRepId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All reps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reps</SelectItem>
            {(figures?.reps ?? []).map((rep) => (
              <SelectItem key={rep.user_id} value={rep.user_id}>
                {rep.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {notFound ? (
        <p className="text-sm text-muted-foreground">
          No published target plan covers this month yet.
        </p>
      ) : (
        <Card className="shadow-md border-gray-300 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-normal">Row</th>
                  <th className="px-4 py-2 font-normal text-right">
                    Contribution
                  </th>
                  <th className="px-4 py-2 font-normal text-right">Revenue</th>
                  <th className="px-4 py-2 font-normal text-right">Target</th>
                  <th className="px-4 py-2 font-normal text-right">
                    % achieved
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isCompany = row.user_id === "company";
                  const pct = pctAchieved(row);
                  return (
                    <tr
                      key={row.user_id}
                      className={`border-b border-border last:border-0 ${
                        isCompany ? "bg-muted/40 font-medium" : ""
                      }`}
                    >
                      <td className="px-4 py-2.5">{row.name}</td>
                      <td className="px-4 py-2.5 text-right">
                        {formatCurrencyGbp(row.contribution_amt)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {formatCurrencyGbp(row.revenue_amt)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {formatCurrencyGbp(row.target_amt)}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right ${pctColorClass(pct)}`}
                      >
                        {row.pct_achieved != null ? `${pct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-muted-foreground"
                    >
                      No figures for this month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
